import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bot, ArrowLeft } from "lucide-react";
import Tabs from "@components/misc/Tabs";
import { useSelector } from "react-redux";
import { selectUserInfo } from "@redux/slices/authSlice";

import InvocationsList from "./Components/Invocations";
import { getIntegrationById } from "@services/httpservice";

import tabsList from "@data/details-tabs.json";

const Invocations = () => {
  const [integrationDetails, setIntegrationDetails] = useState({});
  const [isRunning, setIsRunning] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { id } = useParams();

  const handleStop = () => {
    setIsRunning(!isRunning);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getIntegrationById(id);
        if (data) {
          setIntegrationDetails(data);
        }
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const navigate = useNavigate();
  const userInfo = useSelector(selectUserInfo);

  const handleGoToDashboard = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/integrations/${id}/overview`);
  };

  const onTabSelected = (pageName) => {
    // Use replace:true so tab switching doesn't pollute browser history
    navigate(`/integrations/${id}/${pageName}`, { replace: true });
  };

  return (
    <div className="mx-auto flex h-full min-h-0 max-w-full flex-col overflow-hidden px-4 pt-2 sm:px-8 lg:px-12">
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-x-3 w-full lg:w-auto">
            <button
              onClick={handleGoToDashboard}
              title="Back to listing"
              className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[100px]"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>


            {userInfo && userInfo.role && userInfo.role.toLowerCase() !== "user" && (
              <button
                onClick={handleEdit}
                title="Edit Integration"
                className="flex items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-3 py-2 text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95"
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g
                    id="SVGRepo_tracerCarrier"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></g>
                  <g id="SVGRepo_iconCarrier">
                    {" "}
                    <path
                      d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.40005Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>{" "}
                    <path
                      d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>{" "}
                  </g>
                </svg>
              </button>
            )}

            {userInfo && userInfo.role && userInfo.role.toLowerCase() === "user" && (
              <button
                onClick={handleEdit}
                title="View Integration"
                className="flex items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-3 py-2 text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95"
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                  <g id="SVGRepo_iconCarrier">
                    {" "}
                    <path
                      d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>{" "}
                  </g>
                </svg>
              </button>
            )}

            <h2 className="lg:w-80 text-base sm:text-lg font-bold text-gray-900 tracking-tight truncate">
              {isLoading ? (
                <div className="my-1 animate-pulse">
                  <div className="h-8 w-40 sm:w-80 rounded-full bg-gray-100">
                  </div>
                </div>
              ) : (
                integrationDetails && integrationDetails.name ? integrationDetails.name : <span>&nbsp;</span>
              )}
            </h2>
          </div>

          <div className="flex-1 flex items-center justify-center w-full lg:w-auto my-4 lg:my-0 order-2">
            <div className="w-full lg:w-auto">
              <Tabs tabsList={tabsList} selectedIndex={1} interactive={true} onTabSelected={onTabSelected} />
            </div>
          </div>

          <div className="lg:w-80 flex items-center justify-end gap-x-3 mt-4 sm:mt-0 order-3">
            {userInfo && userInfo.role && userInfo.role.toLowerCase() !== "user" && integrationDetails && (
              <button
                title={integrationDetails.agent_session_id ? "Open creation chat" : "Open Agent Chat"}
                onClick={() => navigate(integrationDetails.agent_session_id ? `/agent?session=${integrationDetails.agent_session_id}` : '/agent')}
                className="flex items-center justify-center gap-x-2 rounded-full bg-blue-600 border border-blue-700 px-5 py-2.5 text-white text-sm font-bold transition-all duration-200 hover:bg-blue-700 shadow-sm active:scale-95 whitespace-nowrap"
              >
                Launch Agent
              </button>
            )}
            {" "}

            {isRunning &&
              <div className="flex items-center gap-3 rounded-full bg-emerald-50 border border-emerald-200 pl-4 pr-1 py-1 overflow-hidden shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black text-emerald-700 uppercase tracking-[0.15em]">{integrationDetails && integrationDetails.status}</span>
                </div>
                <button
                  onClick={handleStop}
                  className="rounded-full bg-red-600 px-6 py-2 text-xs font-black text-white transition-all duration-200 hover:bg-red-700 shadow-lg hover:shadow-red-200 active:scale-95 uppercase tracking-wider"
                >
                  Stop
                </button>
              </div>
            }
            {!isRunning &&
              <div className="flex items-center gap-3 rounded-full bg-red-50 border border-red-200 pl-4 pr-1 py-1 overflow-hidden shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black text-red-700 uppercase tracking-[0.15em]">Stopped</span>
                </div>
                <button
                  onClick={handleStop}
                  className="rounded-full bg-blue-600 px-6 py-2 text-xs font-black text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 uppercase tracking-wider"
                >
                  Start
                </button>
              </div>
            }
          </div>
        </div>
        <div className="mt-4 min-h-0 flex-1 overflow-hidden">
          <InvocationsList details={integrationDetails} isLoading={isLoading} error={error} />
        </div>
      </section>
    </div>
  );
};

export default Invocations;
